import { Application, Container, Graphics } from "pixi.js";
import { DEMO_FORM } from "@talent/tasks";
import type { TaskDefinition } from "@talent/protocol";
import {
  SessionRecorder,
  TouchTracker,
  detectImplausibleSpeed,
  detectMultiTouch,
} from "@talent/client-telemetry";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  drawTile,
  easeOutBack,
  easeOutCubic,
  glow,
  nudge,
  pop,
  prefersReducedMotion,
  ripple,
  untilAbandoned,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime, tick } from "./sound.js";
import { COLOR, familyColor, lighten, mix } from "./paint.js";

/**
 * Spatial span, as a game about lanterns that light up and sing.
 *
 * The mechanic is unchanged and cannot change: a sequence is shown, the child reproduces it, and
 * the longest they reliably reproduce is the measurement. What changed is everything around it.
 * The previous version flashed grey squares in silence — a correct psychometric task, and
 * nothing a seven-year-old would open twice.
 *
 * **The tones are the game.** A sequence you can hear is a tune you can play back; a sequence you
 * can only see is a list of positions to memorise. Notes come from a pentatonic scale so any
 * order sounds musical rather than wrong. That is a change to the STIMULUS and not to feedback,
 * so it is a different instrument — which is why `calibrationVersion` moved to "2" and why every
 * trial now records the calibration it ran under.
 *
 * Unchanged constraints, all from the analysis rather than from taste:
 *  - **No text, no numbers.** Verbal reasoning needs local norming (v2 §15) and is out of scope,
 *    so nothing on screen may smuggle language in. Progress is pips, never a counter.
 *  - **Generous timing.** A span measure, not a speed measure. PC-1 excludes reaction time, so a
 *    slow device changes how long a child waits and never what is measured.
 *  - **Errors end gently** after `stopAfterErrors`, and the answer is then shown rather than
 *    hidden. Frustration must not be recorded as inability (v2 §19).
 *  - **Celebration is asymmetric.** Particles and a chime when right; a small wobble and a soft
 *    two-note fall when wrong. A game that punishes teaches a child to stop trying, which
 *    corrupts the measurement as surely as it upsets them.
 */

export interface SpanTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  /** Highest span length reproduced correctly. Raw and task-local: construct
   *  estimation happens server-side, never here (v2 §7). */
  rawScore: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateStimulus: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => number[];
  /** What the child must tap, given what they were shown. Reversed for backward span. */
  expectedResponse: (task: TaskDefinition, stimulus: number[]) => number[];
  scoreTrial: (expected: number[], response: number[]) => boolean;
  /** Play the wordless demonstration first. Set on a child's first time with this game. */
  showTutorial?: boolean;
  /** Awaited after the demonstration, so the shell can hold with a real dialog. */
  /**
   * Awaited after the demonstration. Resolving with "replay" runs it again.
   *
   * A demonstration that plays once at a speed somebody else chose is lost for good to a child who
   * blinked, and their alternative is guessing through a game whose rule they never saw.
   */
  onTutorialComplete?: () => Promise<"replay" | "start">;
  /** Aborted when the child leaves the game, so the task closes rather than hanging. */
  signal?: AbortSignal;
  /**
   * The level to open at, from the server's suggestion for this child.
   *
   * Absent for free play, which opens at the game's own floor. Clamped into range rather than
   * trusted: it is derived from an objective's level window, which lives in a different file from
   * this task's ceiling.
   */
  startAt?: number;
}

export async function runSpanTask(opts: RunOptions): Promise<SpanTaskResult> {
  const { task, recorder, formId } = opts;
  const cols = Number(task.params["gridCols"]);
  const rows = Number(task.params["gridRows"]);
  const cellCount = cols * rows;
  const itemOnMs = Number(task.params["itemOnMs"]);
  const itemGapMs = Number(task.params["itemGapMs"]);
  const recallDelayMs = Number(task.params["recallDelayMs"]);
  const backward = task.params["recallDirection"] === "backward";
  const accent = familyColor(task.family);
  /**
   * Every sleep inside this task is interruptible.
   *
   * Aliased once rather than threading the signal through a dozen call sites: a task that keeps
   * animating after the child has left is a task whose canvas is still on screen while the next
   * one is being built on top of it.
   */
  const idle = (ms: number) => wait(ms, opts.signal);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    // Cap at 2: on a low-end phone a DPR-3 buffer costs frame budget for no visible benefit at
    // these tile sizes, and frame budget is an ADR-002 exit criterion.
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  // The play area is shared between games, and it must contain exactly one canvas. The route
  // effect already waits for the previous run to tear down, so this should be a no-op — it is
  // here because two stacked canvases both keep their pointer handlers, which presents as taps
  // going to the wrong game and is very hard to see.
  opts.canvasParent.replaceChildren(app.canvas);

  /** Painted ground, so the canvas is a place rather than a black rectangle. */
  const backdrop = new Graphics();
  const glowLayer = new Container();
  const board = new Container();
  /** In front of everything: ripples and particles. */
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, board, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  // ---------------------------------------------------------------------------
  // Layout, recomputed on resize so a rotated phone is not left with the board
  // off the edge of the screen mid-trial.
  // ---------------------------------------------------------------------------
  let tileSize = 0;
  let gap = 0;
  let originX = 0;
  let originY = 0;

  function measure(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    // Room reserved at the bottom for the progress pips.
    const usableH = h - 92;
    gap = Math.max(10, Math.min(w, usableH) * 0.045);
    tileSize = Math.min(
      (w * 0.9 - gap * (cols - 1)) / cols,
      (usableH * 0.9 - gap * (rows - 1)) / rows,
    );
    const boardW = tileSize * cols + gap * (cols - 1);
    const boardH = tileSize * rows + gap * (rows - 1);
    originX = (w - boardW) / 2 + tileSize / 2;
    originY = (usableH - boardH) / 2 + tileSize / 2;
  }

  const centreOf = (index: number) => ({
    x: originX + (index % cols) * (tileSize + gap),
    y: originY + Math.floor(index / cols) * (tileSize + gap),
  });

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    // A vertical wash drawn as bands: cheaper than a shader and indistinguishable at this size.
    const bands = 24;
    for (let i = 0; i < bands; i++) {
      backdrop
        .rect(0, (h / bands) * i, w, h / bands + 1)
        .fill(mix(COLOR.stage, COLOR.stageDeep, i / bands));
    }
    // A halo in the family's colour, so each game's world is tinted like its card on the home
    // screen and a child knows where they are.
    backdrop.circle(w / 2, h * 0.42, Math.min(w, h) * 0.55).fill({ color: accent, alpha: 0.08 });
  }

  // ---------------------------------------------------------------------------
  // Lanterns
  // ---------------------------------------------------------------------------
  const dim = mix(COLOR.stage, COLOR.card, 0.16);
  const tiles: Graphics[] = [];
  type TileState = "dim" | "lit" | "right" | "wrong";

  function drawLantern(g: Graphics, state: TileState): void {
    const color =
      state === "lit"
        ? lighten(accent, 0.12)
        : state === "right"
          ? COLOR.correct
          : state === "wrong"
            ? COLOR.wrong
            : dim;
    drawTile(g, tileSize, color, { lit: state !== "dim", radius: tileSize * 0.26 });
    if (state !== "dim") {
      // An inner circle, so a lit lantern reads as having a filament rather than a brighter fill.
      g.circle(0, 0, tileSize * 0.19).fill({ color: 0xffffff, alpha: 0.3 });
    }
  }

  function applyHitArea(g: Graphics): void {
    // Padded: a six-year-old's tap lands off-centre more often than an adult's, and a near-miss
    // must not be scored as a wrong answer.
    const half = tileSize / 2 + tileSize * 0.16;
    g.hitArea = {
      contains: (hx: number, hy: number) => hx >= -half && hy >= -half && hx <= half && hy <= half,
    };
  }

  measure();
  paintBackdrop();
  for (let i = 0; i < cellCount; i++) {
    const g = new Graphics();
    // Pivot is the centre by construction, since `drawTile` draws around the origin. `pop` needs
    // that: a top-left pivot would make a tapped tile shrink away from the finger.
    const { x, y } = centreOf(i);
    g.position.set(x, y);
    drawLantern(g, "dim");
    g.eventMode = "static";
    g.cursor = "pointer";
    applyHitArea(g);
    tiles.push(g);
    board.addChild(g);
  }

  /**
   * The lanterns arrive rather than appearing.
   *
   * Once, at the start of the task rather than per trial — this grid persists across trials, and
   * re-animating it between them would put a flourish between a child and the sequence they are waiting
   * for. Staggered by index so it reads as a sequence, and skipped under `prefers-reduced-motion`.
   */
  /**
   * How long the arrival takes, so the first trial can wait for it.
   *
   * The last tile starts at `50 + (n-1) * 45` and runs for 260ms. Without waiting, the first lantern
   * of the first sequence lit while the grid was still assembling itself — the child saw a cell flash
   * inside an animation and had no way to tell the flourish from the stimulus. Reported as "the first
   * box lights up before the entrance has finished", and it was exactly that.
   */
  const startedAt = performance.now();
  let entranceMs = 0;
  if (!prefersReducedMotion()) {
    tiles.forEach((tile, i) => {
      tile.scale.set(0);
      void wait(50 + i * 45).then(() => animate(260, (t) => tile.scale.set(easeOutBack(t))));
    });
    entranceMs = 50 + Math.max(0, tiles.length - 1) * 45 + 260;
  }

  // ---------------------------------------------------------------------------
  // Progress pips: one per item in this sequence, filled as the child taps.
  // Pips rather than "3 / 5" because a numeral is text (v2 §15), and because a
  // child who cannot yet read numbers can still count dots.
  // ---------------------------------------------------------------------------
  const pips = new Graphics();
  board.addChild(pips);

  function drawPips(total: number, filled: number): void {
    const w = app.renderer.width;
    const y = app.renderer.height - 48;
    const r = 7;
    const step = 22;
    let x = (w - (total - 1) * step) / 2;
    pips.clear();
    for (let i = 0; i < total; i++) {
      pips.circle(x, y, i < filled ? r + 1.5 : r).fill({
        color: i < filled ? accent : COLOR.card,
        alpha: i < filled ? 1 : 0.22,
      });
      x += step;
    }
  }

  function relayout(): void {
    measure();
    paintBackdrop();
    tiles.forEach((g, i) => {
      const { x, y } = centreOf(i);
      g.position.set(x, y);
      drawLantern(g, "dim");
      applyHitArea(g);
    });
  }
  app.renderer.on("resize", relayout);

  // ---------------------------------------------------------------------------
  // Presentation and recall
  // ---------------------------------------------------------------------------

  /** Lights one lantern, sounds its note, settles it back down. */
  async function showItem(index: number): Promise<void> {
    const g = tiles[index];
    if (!g) return;
    const { x, y } = centreOf(index);

    cellTone(index);
    glow(glowLayer, x, y, tileSize * 0.62, accent, itemOnMs);
    drawLantern(g, "lit");

    if (prefersReducedMotion()) {
      await idle(itemOnMs);
    } else {
      // Swells rather than snapping on, which is what makes a sequence read as a melody being
      // played instead of a series of state changes.
      await animate(itemOnMs, (t) => {
        const swell = t < 0.3 ? easeOutCubic(t / 0.3) : 1 - (t - 0.3) / 0.7;
        g.scale.set(1 + 0.14 * swell);
      });
      g.scale.set(1);
    }
    drawLantern(g, "dim");
    await idle(itemGapMs);
  }

  /**
   * The pip sweep before recall: your turn, and which way round.
   *
   * **Both directions get one, and the forward game used to get nothing.** The backward game needs a
   * cue because the direction cannot be said in words — but a child playing the forward game had the
   * last lantern go out and then silence, with no way to tell "still watching" from "your go". They
   * waited; the trial timed out; the record said they had abandoned it. A cue that appears in one game
   * and not the other is also a difference between two games that are meant to differ only in
   * direction.
   *
   * The sweep runs WITH the reading direction on a forward trial and against it on a backward one, so
   * the same three dots carry both messages at once — go now, and go this way.
   */
  async function showDirectionCue(): Promise<void> {
    const order = backward ? [3, 2, 1] : [1, 2, 3];
    for (const filled of order) {
      drawPips(3, filled);
      tick();
      await idle(150);
    }
    // A beat with the dots gone, so the sweep is a cue and not part of the sequence.
    pips.clear();
    tick();
    await idle(120);
  }

  /** Resolves with the cells the child tapped, in order. */
  function collectResponse(expectedLength: number): {
    response: Promise<number[]>;
    simulateTap: (index: number) => void;
    cleanup: () => void;
  } {
    const tapped: number[] = [];
    let resolveAll: ((r: number[]) => void) | null = null;
    let accepting = true;

    const bound = tiles.map((g, index) => {
      const handler = () => {
        if (!accepting) return;
        const { x, y } = centreOf(index);

        // Feedback on every action, before anything is evaluated. A tap the game is still
        // processing looks like a tap that was ignored, and a child taps again — which the
        // contamination detectors then read as a multi-touch anomaly, so this is a data-quality
        // control as much as a courtesy.
        ripple(fx, x, y, accent, tileSize * 0.8);
        cellTone(index);
        drawLantern(g, "lit");
        void pop(g).then(() => {
          if (accepting) drawLantern(g, "dim");
        });

        tapped.push(index);
        drawPips(expectedLength, tapped.length);

        if (tapped.length >= expectedLength) {
          accepting = false;
          resolveAll?.([...tapped]);
        }
      };
      g.on("pointertap", handler);
      return { g, handler };
    });

    return {
      response: new Promise<number[]>((resolve) => {
        resolveAll = resolve;
      }),
      /** Lets the demonstration tap for itself, through the same handler a finger reaches. */
      simulateTap: (index: number) => bound[index]?.handler(),
      cleanup: () => {
        accepting = false;
        for (const { g, handler } of bound) g.off("pointertap", handler);
      },
    };
  }

  /** Replays the answer slowly after a miss, so the trial teaches instead of only scoring. */
  async function revealAnswer(expected: readonly number[]): Promise<void> {
    for (const index of expected) {
      const g = tiles[index];
      if (!g) continue;
      const { x, y } = centreOf(index);
      drawLantern(g, "right");
      glow(glowLayer, x, y, tileSize * 0.55, COLOR.correct, 320);
      cellTone(index, { volume: 0.09 });
      await idle(250);
      drawLantern(g, "dim");
    }
  }

  // ---------------------------------------------------------------------------
  // Trial loop. Adaptive difficulty, unchanged.
  // ---------------------------------------------------------------------------
  // The level to open at comes from the server's suggestion — the objective the child is working
  // on — and is clamped into this task's range by `newLadder`.
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let bestSpan = 0;
  let reason: SpanTaskResult["reason"] = "completed";

  /**
   * A wordless demonstration, before anything is scored.
   *
   * Only the BACKWARD game needs one, and it needs it badly. Forward span explains itself — the
   * lanterns light in an order and you repeat it. Backward asks for the reverse, and the only cue
   * for that was a pip sweep running the other way, which is a hint if you already know what it
   * hints at. A child who has not been told simply plays it forward and is wrong every time, and
   * the record would read as a child who cannot hold two items.
   *
   * So the game plays a short round itself: two lanterns light, then it taps them back in reverse,
   * with each tap visibly matching the item it undoes. The rule is in the order of the taps.
   *
   * Its own sequence under a `demo` form id, never a scored trial, and not recorded.
   */
  async function playTutorial(): Promise<void> {
    // Looped: the child can watch the reversal again as many times as they want.
    let choice: "replay" | "start" = "start";
    do {
      const stimulus = opts.generateStimulus(task, DEMO_FORM, 0, task.measurement.minDifficulty);
      const expected = opts.expectedResponse(task, stimulus);

      await idle(Math.max(400, entranceMs - (performance.now() - startedAt)));
      drawPips(stimulus.length, 0);
      for (const index of stimulus) {
        if (opts.signal?.aborted) break;
        await showItem(index);
      }
      await showDirectionCue();
      await idle(recallDelayMs);

      const collector = collectResponse(expected.length);
      for (const index of expected) {
        if (opts.signal?.aborted) break;
        // Slower than a child would tap, so the reversal is legible rather than a flurry.
        await idle(620);
        collector.simulateTap(index);
      }
      if (!opts.signal?.aborted) {
        await untilAbandoned(collector.response, opts.signal);
        successChime();
        expected.forEach((index) => {
          const g = tiles[index];
          if (g) drawLantern(g, "right");
        });
        await idle(500);
        choice = (await opts.onTutorialComplete?.()) ?? "start";
      }
      collector.cleanup();
      tiles.forEach((g) => drawLantern(g, "dim"));
      pips.clear();
      if (opts.signal?.aborted) return;
    } while (choice === "replay");
  }

  if (opts.showTutorial && !opts.signal?.aborted) await playTutorial();

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    // Recorded so a trial says which instrument produced it. Without this, a calibration bump
    // would silently pool two instruments into one estimate.
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    for (;;) {
      if (opts.signal?.aborted) {
        reason = "abandoned";
        break;
      }
      const stimulus = opts.generateStimulus(task, formId, trialIndex, difficulty);
      const expected = opts.expectedResponse(task, stimulus);

      recorder.setInTrial(true);
      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        // What was PRESENTED, never the expected answer — on a backward trial those differ, and
        // storing the answer here would make the record unscoreable.
        stimulus,
      });

      // The grid finishes arriving before the first lantern lights. Only ever waits on the first
      // trial: the entrance runs once for the whole task, so by the second it has long elapsed.
      await idle(Math.max(340, entranceMs - (performance.now() - startedAt)));
      drawPips(stimulus.length, 0);
      for (const index of stimulus) {
        // Checked per item: `animate` runs to its full duration, so without this the whole
        // remaining sequence would still play out after the child left.
        if (opts.signal?.aborted) break;
        await showItem(index);
      }
      await showDirectionCue();
      await idle(recallDelayMs);
      if (opts.signal?.aborted) {
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }
      drawPips(stimulus.length, 0);

      const start = performance.now();
      const collector = collectResponse(expected.length);
      const outcome = await untilSettled(collector.response, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - start;
      collector.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        /**
         * Stopping and leaving both end the sitting, and only one of them is recorded.
         *
         * A timeout means the child put the phone down mid-trial. It is `abandoned` rather than a
         * wrong answer on purpose: we know they stopped and we do not know they could not do it, and
         * scoring silence as failure is the mistake `stopAfterErrors` used to make.
         */
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        // No trial_response either way: the child did not respond, and inventing an empty one would
        // record a wrong answer for a trial they never finished.
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }
      const response = outcome;

      const correct = opts.scoreTrial(expected, response);
      if (correct) {
        bestSpan = Math.max(bestSpan, stimulus.length);
        successChime();
        for (const index of response) {
          const { x, y } = centreOf(index);
          burst(fx, x, y, [accent, lighten(accent, 0.4), COLOR.correct]);
          const g = tiles[index];
          if (g) drawLantern(g, "right");
        }
        await idle(560);
      } else {
        missTone();
        await nudge(board);
        await idle(140);
        await revealAnswer(expected);
      }
      tiles.forEach((g) => drawLantern(g, "dim"));
      pips.clear();

      await recorder.record("trial_response", {
        trialIndex,
        response,
        correct,
        // NOT a processing-speed measure (PC-1) — kept only for contamination detection.
        responseMs: elapsed,
      });

      const fast = detectImplausibleSpeed(elapsed, stimulus.length);
      if (fast) await recorder.record("input_anomaly", fast);
      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      /**
       * The teaching policy lives in `ladder.ts`, not here.
       *
       * A streak raises the level, errors lower it, and the sitting ends when the trials run out —
       * never because a child was struggling (R-5). It was copied into all ten renderers before,
       * which is nine chances for one game to end up quietly stricter than the rest.
       */
      const step = advance(task, ladder, correct);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
      await idle(300);
    }
  } catch {
    reason = "error";
  } finally {
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: bestSpan,
    });
    recorder.exitTask();
    app.renderer.off("resize", relayout);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return { reason, trialsAttempted: trialIndex, rawScore: bestSpan };
}
